import React from "react";
import { JetSportBox } from "../../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../../components/BoxView/BoxHeader2";
import {
  ApprovalBreakdownDto,
  ApprovalMetricsDto,
} from "../../../../data/models/responses/StatisticsResponses";

interface Props {
  data: ApprovalMetricsDto | undefined;
  isLoading: boolean;
}

export function ApprovalFunnel({ data, isLoading }: Props) {
  return (
    <JetSportBox
      className="opacity-0 animate-fadeIn h-full"
      style={{ animationDelay: "560ms", animationFillMode: "forwards" }}
    >
      <BoxHeader2 className="!py-5 !px-6 border-b border-borderGray">
        <div className="flex flex-col">
          <span className="text-xxs uppercase tracking-[0.22em] text-gray-600 font-semibold">
            Workflow
          </span>
          <span className="text-base font-semibold text-black mt-0.5">
            Approval breakdown
          </span>
        </div>
      </BoxHeader2>

      <div className="px-6 py-6 space-y-6 flex-1">
        <FunnelRow label="Logos" breakdown={data?.logos} isLoading={isLoading} />
        <FunnelRow label="Designs" breakdown={data?.designs} isLoading={isLoading} />
      </div>
    </JetSportBox>
  );
}

function FunnelRow({
  label,
  breakdown,
  isLoading,
}: {
  label: string;
  breakdown: ApprovalBreakdownDto | undefined;
  isLoading: boolean;
}) {
  const pending = breakdown?.pending ?? 0;
  const approved = breakdown?.approved ?? 0;
  const rejected = breakdown?.rejected ?? 0;
  const total = pending + approved + rejected;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xxs uppercase tracking-[0.18em] text-gray-600 font-semibold">
          {label}
        </span>
        <span className="text-xs text-gray-600 tabular-nums">
          {!isLoading && breakdown && (
            <>
              avg approval{" "}
              <span className="font-semibold text-black">
                {breakdown.avgTimeToApprovalHours.toFixed(1)}h
              </span>
            </>
          )}
        </span>
      </div>

      {isLoading ? (
        <div className="h-3 w-full bg-gray-100 animate-pulse rounded-full" />
      ) : (
        <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-status-success transition-all duration-700"
            style={{ width: `${pct(approved)}%` }}
          />
          <div
            className="h-full bg-status-pending-1 transition-all duration-700"
            style={{ width: `${pct(pending)}%` }}
          />
          <div
            className="h-full bg-status-failed-1 transition-all duration-700"
            style={{ width: `${pct(rejected)}%` }}
          />
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mt-3">
        <Legend
          color="bg-status-success"
          label="Approved"
          value={approved}
          isLoading={isLoading}
        />
        <Legend
          color="bg-status-pending-1"
          label="Pending"
          value={pending}
          isLoading={isLoading}
        />
        <Legend
          color="bg-status-failed-1"
          label="Rejected"
          value={rejected}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function Legend({
  color,
  label,
  value,
  isLoading,
}: {
  color: string;
  label: string;
  value: number;
  isLoading: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${color}`} />
        <span className="text-xxs uppercase tracking-wider text-gray-600 font-medium">
          {label}
        </span>
      </div>
      <span className="text-lg font-bold tabular-nums text-black">
        {isLoading ? "—" : value.toLocaleString()}
      </span>
    </div>
  );
}
